import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { FileText, Plus, Search, MapPin, Calendar, Heart, MessageCircle, Eye } from 'lucide-react';

const Classifieds = () => {
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Anuncios de ejemplo
  const [classifieds, setClassifieds] = useState([
    {
      id: 1,
      title: "Busco pareja para viajar por México",
      description: "Soy una mujer de 28 años buscando alguien para compartir aventuras y conocer lugares increíbles de México. Me gusta la fotografía y la aventura.",
      category: "Viajes",
      location: "Ciudad de México",
      author: "Ana M.",
      age: 28,
      gender: "Mujer",
      date: "2024-01-15",
      contact: "ana@email.com"
    },
    {
      id: 2,
      title: "Amistad para actividades deportivas",
      description: "Hombre de 32 años buscando amigos para practicar deportes como fútbol, tenis o senderismo. Ideal para mantener una vida activa y saludable.",
      category: "Deportes",
      location: "Monterrey",
      author: "Carlos R.",
      age: 32,
      gender: "Hombre",
      date: "2024-01-14",
      contact: "carlos@email.com"
    },
    {
      id: 3,
      title: "Compañía para eventos culturales",
      description: "Busco alguien para asistir a conciertos, exposiciones y eventos culturales. Me apasiona el arte y la música. Edad 25-35 años.",
      category: "Cultura",
      location: "Guadalajara",
      author: "María G.",
      age: 27,
      gender: "Mujer",
      date: "2024-01-13",
      contact: "maria@email.com"
    }
  ]);

  const [newAd, setNewAd] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    contact: ''
  });

  const categories = [
    "Viajes", "Deportes", "Cultura", "Música", "Cocina", "Tecnología", 
    "Arte", "Literatura", "Fotografía", "Otros"
  ];

  const handleInputChange = (e) => {
    setNewAd({
      ...newAd,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const ad = {
      id: Date.now(),
      ...newAd,
      author: user?.name || 'Anónimo',
      age: user?.age || 25,
      gender: user?.gender || 'No especificado',
      date: new Date().toISOString().split('T')[0]
    };
    
    setClassifieds([ad, ...classifieds]);
    setNewAd({
      title: '',
      description: '',
      category: '',
      location: '',
      contact: ''
    });
    setShowForm(false);
  };

  const filteredClassifieds = classifieds.filter(ad => {
    const matchesSearch = ad.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ad.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || ad.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-text">Clasificados</h1>
          </div>
          <p className="text-gray-600">
            Publica y encuentra anuncios para conocer gente con intereses similares
          </p>
        </div>

        {/* Controles */}
        <div className="card mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Búsqueda */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar anuncios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Filtro por categoría */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Todas las categorías</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Botón publicar */}
            <button
              onClick={() => setShowForm(!showForm)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Publicar Anuncio</span>
            </button>
          </div>
        </div>

        {/* Formulario para publicar */}
        {showForm && (
          <div className="card mb-8">
            <h2 className="text-xl font-semibold text-text mb-6">Publicar Nuevo Anuncio</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Título del anuncio</label>
                  <input
                    type="text"
                    name="title"
                    value={newAd.title}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                    placeholder="Ej: Busco pareja para viajar"
                  />
                </div>

                <div>
                  <label className="form-label">Categoría</label>
                  <select
                    name="category"
                    value={newAd.category}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Ubicación</label>
                  <input
                    type="text"
                    name="location"
                    value={newAd.location}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                    placeholder="Ej: Ciudad de México"
                  />
                </div>

                <div>
                  <label className="form-label">Contacto</label>
                  <input
                    type="email"
                    name="contact"
                    value={newAd.contact}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Descripción</label>
                <textarea
                  name="description"
                  value={newAd.description}
                  onChange={handleInputChange}
                  rows="4"
                  className="input-field"
                  required
                  placeholder="Describe qué buscas, tus intereses, etc..."
                />
              </div>

              <div className="flex space-x-4">
                <button type="submit" className="btn-primary">
                  Publicar Anuncio
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-outline"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista de anuncios */}
        <div>
          <h2 className="text-xl font-semibold text-text mb-6">
            Anuncios ({filteredClassifieds.length})
          </h2>

          {filteredClassifieds.length === 0 ? (
            <div className="card text-center py-12">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-text mb-2">
                No se encontraron anuncios
              </h3>
              <p className="text-gray-600">
                Intenta ajustar tus filtros de búsqueda o publica el primer anuncio
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredClassifieds.map((ad) => (
                <div key={ad.id} className="card hover:shadow-lg transition-shadow duration-200">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-text mb-2">{ad.title}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className="px-2 py-1 bg-red-50 text-primary rounded-full text-xs font-medium">
                          {ad.category}
                        </span>
                        <div className="flex items-center space-x-1">
                          <MapPin className="h-3 w-3" />
                          <span>{ad.location}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3" />
                          <span>{ad.date}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4 line-clamp-3">
                    {ad.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Heart className="h-4 w-4" />
                      <span>{ad.author}, {ad.age} años</span>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button className="p-2 text-gray-400 hover:text-primary transition-colors duration-200">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-primary transition-colors duration-200">
                        <MessageCircle className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Classifieds;
